import { type FC, memo, useMemo } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading } from "@stubhub/broadway-web";
import type { CityLink } from "src/http/types";
import { FormatContent, useContent } from "src/shared/components/content";
import { LinkItem } from "./LinkItem";
import { linksGrid, linksHeaderWrapper } from "./styles";

interface Props {
  cityLinks: readonly CityLink[];
  categoryName: string;
  categoryUrl: string;
  totalEventCount: number;
  showAllLocationsButton: boolean;
}

export const CityLinks: FC<Props> = memo(
  ({ cityLinks, categoryName, categoryUrl, totalEventCount, showAllLocationsButton }) => {
    const isMobile = !useMediaQuery(Breakpoint.Tablet);
    const sortedCityLinks = useMemo(
      () =>
        isMobile
          ? cityLinks.slice(0, 6)
          : cityLinks.toSorted(({ name: nameA = "" }, { name: nameB = "" }) =>
              nameA && nameB ? nameA.localeCompare(nameB) : 0,
            ),
      [cityLinks, isMobile],
    );

    /** "All Locations" */
    const allLocationsText = useContent("cid$#3833994#");

    return (
      <>
        <div className={linksHeaderWrapper}>
          <Heading as="h2" variant="header">
            {/* "{0} events in all locations" */}
            <FormatContent id="cid$#3833382#" params={categoryName} />
          </Heading>
        </div>
        <div className={linksGrid}>
          {showAllLocationsButton && (
            <LinkItem name={allLocationsText} url={categoryUrl} eventCount={totalEventCount} />
          )}
          {sortedCityLinks.map(({ name, stateProvince, url, eventCount = 0 }) => (
            <LinkItem
              key={`${name}_${stateProvince}`}
              name={name ?? ""}
              stateProvince={stateProvince ?? ""}
              url={url ?? ""}
              eventCount={eventCount}
            />
          ))}
        </div>
      </>
    );
  },
);
CityLinks.displayName = "CityLinks";
